/** The error envelope every AGOS endpoint answers with (BACKEND.md §8). */
export interface ApiErrorEnvelope {
  error: string
  code?: string
}

function readStringField(value: unknown, key: string): string | undefined {
  if (!value || typeof value !== 'object') return undefined
  const candidate = (value as Record<string, unknown>)[key]
  if (typeof candidate !== 'string') return undefined
  const trimmed = candidate.trim()
  return trimmed === '' ? undefined : trimmed
}

function truncate(text: string, maxLength = 300): string {
  return text.length > maxLength ? `${text.slice(0, maxLength - 1)}…` : text
}

function buildDeveloperMessage(response: Response, data: unknown): string {
  const prefix = `HTTP ${response.status} ${response.statusText}`
  if (typeof data === 'string') {
    const text = data.trim()
    return text ? `${prefix}: ${truncate(text)}` : prefix
  }
  const envelopeMessage = readStringField(data, 'error') ?? readStringField(data, 'message')
  return envelopeMessage ? `${prefix}: ${envelopeMessage}` : prefix
}

export class ApiError<T = unknown> extends Error {
  override readonly name = 'ApiError'
  readonly status: number
  readonly statusText: string
  readonly data: T | null
  readonly headers: Headers
  readonly response: Response
  readonly method: string
  readonly url: string

  constructor(response: Response, data: T | null, request: { method: string; url: string }) {
    super(buildDeveloperMessage(response, data))
    Object.setPrototypeOf(this, new.target.prototype)
    this.status = response.status
    this.statusText = response.statusText
    this.data = data
    this.headers = response.headers
    this.response = response
    this.method = request.method
    this.url = response.url || request.url
  }

  /** The `code` discriminator, when the backend sent one. */
  get code(): string | undefined {
    return readStringField(this.data, 'code')
  }
}

export class ResponseParseError extends Error {
  override readonly name = 'ResponseParseError'
  readonly status: number
  readonly response: Response
  readonly method: string
  readonly url: string
  readonly rawBody: string
  override readonly cause: unknown

  constructor(
    response: Response,
    rawBody: string,
    cause: unknown,
    request: { method: string; url: string },
  ) {
    super(
      `Failed to parse the response to ${request.method} ${response.url || request.url} ` +
        `(${response.status} ${response.statusText}) as JSON`,
    )
    Object.setPrototypeOf(this, new.target.prototype)
    this.status = response.status
    this.response = response
    this.method = request.method
    this.url = response.url || request.url
    this.rawBody = rawBody
    this.cause = cause
  }
}

export function isApiError(error: unknown): error is ApiError {
  return error instanceof ApiError
}

/**
 * The string to put in front of a user.
 *
 * The backend's Slovak message is shown as-is — FRONTEND.md §6 — with no HTTP
 * status glued to the front and no rewording. `fallback` covers the cases the
 * envelope cannot: a network drop, or a proxy answering HTML.
 */
export function apiErrorMessage(error: unknown, fallback: string): string {
  if (isApiError(error)) {
    return readStringField(error.data, 'error') ?? fallback
  }
  return fallback
}

/**
 * Any string the error envelope carries, by name.
 *
 * `apiErrorMessage` covers the one field almost every failure has. Some carry another the screen needs:
 * a 429 from `/mini-game/play` sends `nextPlayAt` **so that the screen can count down to it**, and a
 * refusal without it leaves the customer counting down to nothing.
 *
 * Here rather than in a consumer because `ApiError` is here, and six copies of this read the wrong field
 * once already — FB-C40. One place to be wrong.
 */
export function apiErrorField(error: unknown, field: string): string | null {
  if (isApiError(error)) return readStringField(error.data, field) ?? null
  // A hand-thrown object in a test, or a shape from before this client — read it rather than go quiet.
  if (typeof error === 'object' && error !== null) {
    const loose = error as { data?: unknown; body?: unknown }
    return readStringField(loose.data, field) ?? readStringField(loose.body, field) ?? null
  }
  return null
}

/**
 * Whether the request reached the server at all.
 *
 * A connection that never arrived and a request the server refused are different things to somebody
 * looking at the screen: one is *try again*, the other is *this will not work however many times you
 * try*.
 */
/**
 * Whether the server refused this for want of a right, rather than for anything the screen sent.
 *
 * **§9.341.** A 403 and a 400 want different sentences: a 400 usually describes the request, and a
 * screen can repeat the server's own words; a 403 describes the *person*, and the server's wording
 * for that is written for a developer - `OrderUpdateService`'s is *"Použite /confirm-status"*, which
 * a dispatcher was shown in a toast. A screen that can tell the two apart can answer *who may do
 * this* instead.
 *
 * Here rather than in a consumer for the same reason as `apiErrorField`: `ApiError` is here, and one
 * place to be wrong is the point.
 */
export function isForbidden(error: unknown): boolean {
  return isApiError(error) && error.status === 403
}

export function reachedServer(error: unknown): boolean {
  return isApiError(error) || (typeof error === 'object' && error !== null && 'response' in error)
}
